import i18n from "i18next";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faBan, faBoxOpen, faShare, faTools, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { AavoDataGridRowContextMenuParams } from "src/components/common/dataGrid/types.tsx";
import { ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto } from "src/api/generated/erp/project/projectActivityMaterial/api/projectActivityMaterialsDataGridApi.ts";
import { ProjectActivityMaterialActionApi } from "src/api/generated/erp/project/projectActivityMaterial/api/projectActivityMaterialActionApi.ts";
import { createProjectActivityMaterialPartConfiguratorType } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeCreators.ts";
import { getConfiguratorFormDialogTitle } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { ProjectActivityMaterialConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/ProjectActivityMaterialConfiguratorView.tsx";
import { OpenPartConfigurationButton } from "src/components/views/erp/configurator/inspecting/OpenPartConfigurationButton.tsx";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { OpenShopOrdersOfSourceButton } from "src/components/views/erp/utilComponents/OpenShopOrdersOfSourceButton.tsx";
import { OpenInventoryProfileButton } from "../../../utilComponents/OpenInventoryProfileButton.tsx";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectActivityMaterialRowContextMenuProps extends AavoDataGridRowContextMenuParams<ProjectActivityMaterialsDataGridApi_ProjectActivityMaterialDto> {
	refreshData: () => Promise<unknown>;
}

export const ProjectActivityMaterialRowContextMenu = ({
	row,
	allSelectedRows,
	onlySingleRowSelected,
	refreshData,
}: ProjectActivityMaterialRowContextMenuProps) => {
	const { openDialog } = useGenericDialog();
	const showConfirmDialog = useConfirmDialog();
	const hasPermission = useHasPermission();
	const allSelectedRowsCanBeUnpacked =
		allSelectedRows.length > 0 &&
		allSelectedRows.every(
			(selectedRow) =>
				selectedRow.acquisitionMethod === "SPLIT_BOM" &&
				selectedRow.state === "INITIAL" &&
				selectedRow.partConfigurationId != null &&
				selectedRow.childLines.length === 0,
		);

	return [
		hasPermission(Permissions.Projects.ProjectMaterials.RELEASE) &&
			allSelectedRows.some((r) => r.state === "INITIAL") && (
				<AsyncMenuButton
					key={"release"}
					label={i18n.t("release")}
					icon={faShare}
					onClick={async () => {
						const confirmed = await showConfirmDialog({
							message: i18n.t("confirm_release_project_activity_materials"),
						});
						if (!confirmed) return;
						await ProjectActivityMaterialActionApi.releaseMaterials({
							projectActivityMaterialIds: allSelectedRows.map((r) => r.projectActivityMaterialId),
						});
						await refreshData();
					}}
				/>
			),
		<AsyncButton
			key={"cancel"}
			label={i18n.t("cancel")}
			icon={faBan}
			variant={"menu"}
			onClick={async () => {
				const confirmed = await showConfirmDialog({
					message: i18n.t("confirm_cancel_project_activity_material_lines"),
				});
				if (!confirmed) return;
				await ProjectActivityMaterialActionApi.cancelMaterials({
					projectActivityMaterialIds: allSelectedRows.map((r) => r.projectActivityMaterialId),
				});
				await refreshData();
			}}
		/>,
		onlySingleRowSelected && row.partIsConfigurable && (
			<AsyncMenuButton
				key={"configure"}
				label={i18n.t("configure")}
				icon={faTools}
				disabled={row.state !== "INITIAL"}
				onClick={async () => {
					const configuratorType = createProjectActivityMaterialPartConfiguratorType(
						row.projectActivityMaterialId,
					);
					const dialogTitle = await getConfiguratorFormDialogTitle({ configuratorType });
					openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
						title: dialogTitle,
						content: (
							<ProjectActivityMaterialConfiguratorView
								configuratorType={configuratorType}
								closeDialog={closeDialog}
								refreshSourceView={refreshData}
								onDataDirtyStateChanged={onDataDirtyStateChanged}
							/>
						),
						agentContext: {
							key: defineAgentUiContextKey("erp.project.projectActivityMaterialConfigurator.dialog"),
							title: i18n.t("configure"),
							pathSegments: [`${i18n.t("line_no")} ${row.lineNumber}`],
							data: {
								projectActivityMaterialId: row.projectActivityMaterialId,
								activityId: row.activityId,
							},
						},
					}));
				}}
			/>
		),
		onlySingleRowSelected && (
			<OpenPartConfigurationButton
				key={"openConfiguration"}
				partConfigurationId={row.partConfigurationId}
				partConfiguratorType={{
					type: "projectActivityMaterial",
					activityMaterialLineId: row.projectActivityMaterialId,
					explicitPartConfigurationId: null,
				}}
			/>
		),
		allSelectedRowsCanBeUnpacked && (
			<AsyncMenuButton
				icon={faBoxOpen}
				key={"unpackConfiguration"}
				label={i18n.t("unpack_configuration")}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						title: i18n.t("are_you_sure"),
						message: i18n.t("unpack_configuration") + "?",
					});
					if (!confirmed) return;

					await ProjectActivityMaterialActionApi.unpackConfiguration({
						projectActivityMaterialIds: allSelectedRows.map(
							(selectedRow) => selectedRow.projectActivityMaterialId,
						),
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected &&
			row.acquisitionMethod === "SPLIT_BOM" &&
			row.state === "INITIAL" &&
			row.partConfigurationId != null &&
			row.childLines.length > 0 &&
			row.childLines.every((r) => r.state === "INITIAL" || r.state === "CANCELLED") && (
				<AsyncMenuButton
					icon={faUndo}
					key={"revertUnpackConfiguration"}
					label={i18n.t("revert_configuration_unpacking")}
					onClick={async () => {
						const confirmed = await showConfirmDialog({
							message: i18n.t("revert_configuration_unpacking_confirmation"),
						});
						if (!confirmed) return;

						await ProjectActivityMaterialActionApi.revertUnpackConfiguration({
							projectActivityMaterialId: row.projectActivityMaterialId,
						});
						await refreshData();
					}}
				/>
			),
		onlySingleRowSelected && hasPermission(Permissions.Purchasing.PurchaseOrders.READ) && (
			<OpenPurchaseOrderButton key="openPurchaseOrder" purchaseOrderId={row.purchaseOrderId} />
		),
		onlySingleRowSelected && row.acquisitionMethod === "MANUFACTURE" && row.state !== "INITIAL" && (
			<OpenShopOrdersOfSourceButton
				key={"openShopOrder"}
				shopOrderSource={{
					sourceType: "PROJECT_ACTIVITY_MATERIAL",
					sourceId: row.projectActivityMaterialId,
				}}
			/>
		),
		onlySingleRowSelected && hasPermission(Permissions.Warehouse.Inventory.READ) && (
			<OpenInventoryProfileButton key={"openInventoryLevelProfile"} partId={row.partId} />
		),
		onlySingleRowSelected && (
			<OpenObjectChangeLogButton
				key={"changeLog"}
				objectRef={{ objectType: "PROJECT_ACTIVITY_MATERIAL", objectId: row.projectActivityMaterialId }}
			/>
		),
	];
};
